import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Card, CardBody, CardHeader, Row, Col, Button } from 'reactstrap';
import { createHashHistory } from 'history';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../common/classes/entityComposition';
import checkBrowser from '../../utils/checkBrowser';
import StatusLoader from '../common/statusLoader';
import reportTypeSelector from './reportTypeSelector';
import { canEditSelector } from './reportSelectors';
import ReportContainer from './reportContainer';
import Icon from '../common/containers/icon';
import { getIsPermittedWithAnyOfThesePermissions, isCreator } from '../common/classes/permissions';
import PDF from '../common/pdf/pdf';
const hashHistory = createHashHistory();
const handleBack = () => {
  hashHistory.replace('/file-cabinet');
};
const calculatePercentage = function calculatePercentage(timeElapsed) {
  let totalTime = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 600;
  if (timeElapsed < 10) {
    return Math.floor(2.5 * timeElapsed);
  }
  return Math.floor(timeElapsed / totalTime * 0.75 * 100) + 24;
};
const Report = _ref => {
  let reportTypeFilterAddActions = _ref.reportTypeFilterAddActions,
    hasAnyDynamicReportTypes = _ref.hasAnyDynamicReportTypes,
    reportTypeFilterAdd = _ref.reportTypeFilterAdd,
    notificationActions = _ref.notificationActions,
    _ref$canEdit = _ref.canEdit,
    canEdit = _ref$canEdit === void 0 ? false : _ref$canEdit,
    reportActions = _ref.reportActions,
    report = _ref.report,
    match = _ref.match;
  const id = match.params.id;
  const active = report.active,
    error = report.error,
    loading = report.loading;
  const indexQuery = reportTypeFilterAdd.indexQuery,
    list = reportTypeFilterAdd.list,
    reportTypeLoading = reportTypeFilterAdd.loading;
  const serverIndex = reportTypeFilterAddActions.serverIndex;
  if (!id) {
    throw new Error('props.params.id is required');
  }
  const _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    loadedPercentage = _useState2[0],
    setLoadedPercentage = _useState2[1];
  const loadedTime = useRef(new Date()).current;
  const timerInterval = useRef(null);
  const handleEdit = useCallback(() => {
    hashHistory.replace("/reports/edit/".concat(id));
  }, [id]);

  // Separate the timer logic into its own useEffect to avoid dependency cycles
  useEffect(() => {
    const loadTime = () => {
      if (active && active.signedURL) {
        clearInterval(timerInterval.current);
        return;
      }
      const timeElapsed = (new Date() - loadedTime) / 1000;
      const newLoadedPercentage = calculatePercentage(timeElapsed);
      setLoadedPercentage(newLoadedPercentage);
    };

    // Start the timer
    timerInterval.current = setInterval(loadTime, 100);

    // Clean up the timer when component unmounts or when active.signedURL becomes available
    return () => {
      clearInterval(timerInterval.current);
    };
  }, [active, loadedTime]); // Only re-create the timer when active changes

  // Separate the data fetching into its own useEffect
  useEffect(() => {
    // Fetch the report data
    reportActions.serverRenderReport(id);

    // Fetch report types if needed
    if (!list.length && !reportTypeLoading) {
      serverIndex(indexQuery);
    }

    // Clean up when unmounting
    return () => {
      reportActions.showCancel(); // clear the active report when navigating away
    };
  }, [id, reportActions, serverIndex, indexQuery, list.length, reportTypeLoading]);
  const showEdit = hasAnyDynamicReportTypes && (active === null || active === void 0 ? void 0 : active.content) !== null && typeof (active === null || active === void 0 ? void 0 : active.content) !== 'undefined';
  let reportTitle = '';
  let url = 'invalidURL';
  let docTitle = '';
  if (active && active.reportType && active.reportType.name && active.reportNumber) {
    docTitle = "".concat(active.reportType.name, " ").concat(active.reportNumber);
    reportTitle = /*#__PURE__*/React.createElement("h3", {
      className: "t-r-title"
    }, docTitle, "\xA0(", /*#__PURE__*/React.createElement("span", {
      style: active.statusName === 'Rejected' ? {
        color: 'red'
      } : {}
    }, active.statusName), ")");
  }
  if (id && active && active.signedURL) {
    url = active.signedURL;
  }
  const buttonStyle = {
    marginRight: '5px',
    float: 'left'
  };
  let title = 'Report not editable.';
  let disabled = true;
  const canEditInReadOnly = getIsPermittedWithAnyOfThesePermissions(['report_read_only']) && !isCreator(active === null || active === void 0 ? void 0 : active.appCreatorID);
  if (canEdit) {
    disabled = false;
    title = 'Edit report';
  }
  if (canEditInReadOnly) {
    disabled = true;
  }
  const isMobileOS = checkBrowser.isMobile();
  const editButton = /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    className: "t-r-edit",
    title: title,
    disabled: disabled,
    onClick: handleEdit,
    style: buttonStyle
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "pencil-alt"
  }), " Edit");
  const header = /*#__PURE__*/React.createElement("div", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: showEdit && !isMobileOS ? 3 : 2
  }, /*#__PURE__*/React.createElement(Button, {
    className: "t-r-back",
    size: "sm",
    onClick: handleBack,
    style: buttonStyle
  }, "File Cabinet"), showEdit && !isMobileOS ? editButton : null), /*#__PURE__*/React.createElement(Col, {
    sm: showEdit ? 9 : 10
  }, reportTitle)));
  let content = /*#__PURE__*/React.createElement(StatusLoader, {
    percentage: loadedPercentage,
    loaded: !active
  });
  if (active && active.signedURL) {
    content = /*#__PURE__*/React.createElement(PDF, {
      className: "t-doc-report-detail-pdf",
      title: docTitle,
      src: url,
      button: editButton,
      hideTitleIfMobile: true,
      nativeRenderer: true,
      containerMaxHeight: "900px"
    });
  } else if (!loading) {
    content = /*#__PURE__*/React.createElement("div", {
      style: {
        paddingLeft: '20px'
      }
    }, /*#__PURE__*/React.createElement("h3", null, "There was a problem rendering this report"), /*#__PURE__*/React.createElement("p", null, "Please try again later"));
  }

  // Handle error notifications
  useEffect(() => {
    if (error) {
      // print the error msg if possible
      if (error.data && error.data.error_msg) {
        notificationActions.error({
          message: error.data.error_msg,
          id: 'report_render_error'
        });
      }
      // print the whole error
      notificationActions.error({
        message: error
      });
    }
  }, [error, notificationActions]);
  return /*#__PURE__*/React.createElement(ReportContainer, null, /*#__PURE__*/React.createElement(Card, {
    style: {
      height: '1000px'
    }
  }, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading-tight"
  }, header), /*#__PURE__*/React.createElement(CardBody, null, content)));
};
Report.propTypes = {
  hasAnyDynamicReportTypes: PropTypes.bool.isRequired,
  report: PropTypes.shape({
    active: PropTypes.shape({
      appCreatorID: PropTypes.string,
      signedURL: PropTypes.string,
      content: PropTypes.shape({}),
      reportType: PropTypes.shape({
        name: PropTypes.string
      }),
      reportNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      statusName: PropTypes.string
    }),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool
  }).isRequired,
  reportActions: PropTypes.shape({
    serverRenderReport: PropTypes.func,
    showCancel: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  canEdit: PropTypes.bool,
  reportTypeFilterAdd: PropTypes.shape({
    loading: PropTypes.bool,
    list: PropTypes.arrayOf(PropTypes.shape({})),
    indexQuery: PropTypes.shape({
      sortDirection: PropTypes.string
    }).isRequired
  }).isRequired,
  reportTypeFilterAddActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired
};
export { Report as TestReport };
export default entityComposition(Report, ['report', 'reportTypeFilterAdd'], {
  hasAnyDynamicReportTypes: state => reportTypeSelector.getDynamicReportTypes(state).length > 0,
  canEdit: canEditSelector
}, ['notification']);